import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Loader2, Search, Users, Sparkles, ChevronRight, UploadCloud, CheckCircle2, XCircle, FileUp, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { releaseStatementLoads } from '../../lib/releaseStatementLoads';
import { useToast } from '../../contexts/ToastContext';

interface GeneratedContractorStatement {
  id: string;
  driver_name: string;
  truck_number: string;
  week_start_date: string;
  week_end_date: string;
  // Financials used in the per-driver card. Some rows may not have every
  // column depending on which migrations are deployed.
  total_revenue?: number | null;
  total_deductions?: number | null;
  total_fuel?: number | null;
  total_credits?: number | null;
  net_pay: number;
  // contractor_name on the statement row (migration 20260422000002). When
  // unavailable, we fall back to the driver's scheduled-deduction entry.
  contractor_name?: string | null;
  contractor_address?: string | null;
  // imported_file_* mark rows that came from the AI importer.
  imported_file_name?: string | null;
  imported_file_path?: string | null;
  created_at: string;
}

interface CompanyRow {
  id: string;
  name: string;
  code?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  phone?: string | null;
  payer_tin?: string | null;
}

const ALL_TAB = '__all__';
const UNASSIGNED_TAB = '__unassigned__';

const BUCKET = 'company-documents';

// PostgREST returns a variety of codes when a queried column or table isn't
// present yet (pending migration, stale schema cache, etc). Treat all of them
// as "missing" so we can fall back to a narrower column set.
function isMissingColumnErr(err: any): boolean {
  if (!err) return false;
  const code = err.code;
  return code === '42703' || code === '42P01' || code === 'PGRST204' || code === 'PGRST205';
}

// Cache column-presence discoveries across loads to avoid firing the same
// doomed 400-bound SELECT on every navigation / year change.
let hasStatementExtendedCols: boolean | null = null;
let hasStatementMidCols: boolean | null = null;
let hasCompanyPayerCols: boolean | null = null;

export function Company1099View() {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [generated, setGenerated] = useState<GeneratedContractorStatement[]>([]);
  // Per-driver scheduled_deduction info (type, business name, commission) —
  // used by the per-driver rollup to classify Company Driver vs Owner Operator
  // and to compute "After X%" when commission is a percentage.
  const [driverInfoMap, setDriverInfoMap] = useState<Record<string, { lease_or_rent: string; contractor_name?: string; contractor_address?: string; commission_cpm?: string }>>({});
  // driver_1099_info rows (keyed by lowercased driver_name). Primary source
  // for the 1099-NEC contractor identity — importer writes here, the detail
  // page writes here, and the main list classifies from here.
  const [taxInfoMap, setTaxInfoMap] = useState<Record<string, { contractor_name?: string; use_contractor_on_1099?: boolean }>>({});
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  // driver_name (lowercased, trimmed) → company_id
  const [driverCompany, setDriverCompany] = useState<Record<string, string>>({});
  const [selectedCompanyTab, setSelectedCompanyTab] = useState<string>(ALL_TAB);
  // Payer info edit state — keyed by company id currently being edited.
  const [editingPayer, setEditingPayer] = useState<string | null>(null);
  const [payerDraft, setPayerDraft] = useState<{ payer_tin: string; address: string; city: string; state: string; zip_code: string; phone: string }>({
    payer_tin: '', address: '', city: '', state: '', zip_code: '', phone: '',
  });
  const [savingPayer, setSavingPayer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // AI-assisted batch upload of legacy statement PDFs.
  const [aiBatchBusy, setAiBatchBusy] = useState(false);
  const [aiBatchStatus, setAiBatchStatus] = useState<{ file: string; state: 'pending' | 'uploading' | 'parsing' | 'done' | 'error'; error?: string }[]>([]);

  useEffect(() => { loadAll(); }, [year]);

  async function loadAll() {
    try {
      setLoading(true);
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      const sdRes = await supabase
        .from('scheduled_deductions')
        .select('driver_name, lease_or_rent, contractor_name, contractor_address, commission_cpm, created_at')
        .order('created_at', { ascending: false });
      if (sdRes.error) throw sdRes.error;

      // Request the full rich column set (gross/deductions/fuel/credits +
      // contractor_name + imported_file_name). Fall back progressively if
      // individual migrations aren't deployed yet. Module-level flags cache
      // what we've already learned so we don't fire a doomed rich SELECT
      // on every re-mount.
      // We skip imported_file_* AND contractor_name in the SELECT to dodge
      // PostgREST schema-cache 400s on managed Supabase. imported_file_* are
      // filled from storage listing below; contractor_name falls back to the
      // driver's scheduled_deductions entry (via infoMap).
      const baseCols = 'id, driver_name, truck_number, week_start_date, week_end_date, net_pay, created_at';
      const midCols = `${baseCols}, total_revenue, total_deductions, total_fuel, total_credits`;
      const richCols = midCols;
      const queryStatements = (cols: string) => supabase
        .from('driver_statements')
        .select(cols)
        .gte('week_start_date', yearStart)
        .lte('week_end_date', yearEnd)
        .order('week_start_date', { ascending: false });
      let statementsRes: any;
      if (hasStatementExtendedCols === false && hasStatementMidCols === false) {
        statementsRes = await queryStatements(baseCols);
      } else if (hasStatementExtendedCols === false) {
        statementsRes = await queryStatements(midCols);
      } else {
        statementsRes = await queryStatements(richCols);
        if (!statementsRes.error) hasStatementExtendedCols = true;
      }
      if (isMissingColumnErr(statementsRes.error)) {
        hasStatementExtendedCols = false;
        statementsRes = await queryStatements(midCols);
        if (!statementsRes.error) hasStatementMidCols = true;
      }
      if (isMissingColumnErr(statementsRes.error)) {
        hasStatementMidCols = false;
        // Last resort: base columns only.
        statementsRes = await queryStatements(baseCols);
      }
      if (statementsRes.error) throw statementsRes.error;

      const infoMap: Record<string, { lease_or_rent: string; contractor_name?: string; contractor_address?: string; commission_cpm?: string }> = {};
      (sdRes.data || []).forEach((r: any) => {
        const key = (r.driver_name || '').trim().toLowerCase();
        if (!key || !r.lease_or_rent) return;
        if (!infoMap[key]) {
          infoMap[key] = {
            lease_or_rent: r.lease_or_rent,
            contractor_name: r.contractor_name || undefined,
            contractor_address: r.contractor_address || undefined,
            commission_cpm: r.commission_cpm || undefined,
          };
        }
      });

      // Show every driver statement for the year — the 1099 view rolls up
      // annual payouts across everyone, not just contractors.
      const contractorStatements = (statementsRes.data || [])
        .map((s: any): GeneratedContractorStatement => {
          const info = infoMap[(s.driver_name || '').trim().toLowerCase()];
          return {
            ...s,
            // Prefer contractor_name stamped on the statement itself; fall
            // back to the driver's current scheduled-deduction entry.
            contractor_name: s.contractor_name || info?.contractor_name || null,
            contractor_address: s.contractor_address || info?.contractor_address || null,
          };
        });

      // Fill imported_file_* via storage-bucket listing. This is
      // schema-cache-independent, so the "N imported" badge and Preview
      // buttons work even when PostgREST doesn't know about the columns yet.
      try {
        const yearPath = `1099/imports/${year}`;
        const { data: files } = await supabase.storage
          .from(BUCKET)
          .list(yearPath, { limit: 1000 });
        if (files && files.length) {
          type FileEntry = { path: string; name: string; ts: number };
          const indexed: FileEntry[] = [];
          for (const f of files) {
            const match = f.name.match(/^(\d{10,13})-(.+)$/);
            if (!match) continue;
            const ts = Number(match[1]);
            if (!Number.isFinite(ts)) continue;
            indexed.push({ path: `${yearPath}/${f.name}`, name: match[2], ts });
          }
          for (const row of contractorStatements) {
            if (row.imported_file_path) continue;
            const rowTs = new Date(row.created_at).getTime();
            let best: FileEntry | null = null;
            let bestDelta = 15 * 60 * 1000;
            for (const f of indexed) {
              const d = Math.abs(f.ts - rowTs);
              if (d < bestDelta) { best = f; bestDelta = d; }
            }
            if (best) {
              row.imported_file_path = best.path;
              row.imported_file_name = best.name;
            }
          }
        }
      } catch (err) {
        console.warn('Storage-bucket fallback for imported statement PDFs failed:', err);
      }

      setGenerated(contractorStatements);
      setDriverInfoMap(infoMap);

      // Load driver_1099_info rows so we can classify contractors even when
      // they have no scheduled_deductions entry. Silent fallback if the
      // contractor_* / use_contractor_on_1099 columns aren't deployed yet.
      try {
        let dtRes: any = await supabase
          .from('driver_1099_info')
          .select('driver_name, contractor_name, use_contractor_on_1099');
        if (isMissingColumnErr(dtRes.error)) {
          dtRes = await supabase
            .from('driver_1099_info')
            .select('driver_name, contractor_name');
        }
        if (isMissingColumnErr(dtRes.error)) {
          dtRes = { data: [], error: null };
        }
        const tMap: Record<string, { contractor_name?: string; use_contractor_on_1099?: boolean }> = {};
        for (const row of (dtRes.data || []) as any[]) {
          const k = (row.driver_name || '').trim().toLowerCase();
          if (!k) continue;
          tMap[k] = {
            contractor_name: row.contractor_name || undefined,
            use_contractor_on_1099: !!row.use_contractor_on_1099,
          };
        }
        setTaxInfoMap(tMap);
      } catch (err) {
        console.warn('Could not load driver_1099_info for classification:', err);
        setTaxInfoMap({});
      }

      // Companies tab bar — load active companies with their payer info. If
      // payer_tin / phone columns are missing (migration not yet deployed),
      // select minimal fields and silently fall back. Skip the rich probe if
      // we already learned the columns are missing this session.
      const richCompanyCols = 'id, name, code, address, city, state, zip_code, phone, payer_tin';
      const baseCompanyCols = 'id, name, code, address, city, state, zip_code';
      let cRes: any;
      if (hasCompanyPayerCols === false) {
        cRes = await supabase
          .from('companies')
          .select(baseCompanyCols)
          .eq('is_active', true)
          .order('name');
      } else {
        cRes = await supabase
          .from('companies')
          .select(richCompanyCols)
          .eq('is_active', true)
          .order('name');
        if (!cRes.error) hasCompanyPayerCols = true;
      }
      if (isMissingColumnErr(cRes.error)) {
        hasCompanyPayerCols = false;
        cRes = await supabase
          .from('companies')
          .select(baseCompanyCols)
          .eq('is_active', true)
          .order('name');
      }
      if (cRes.error) throw cRes.error;
      setCompanies((cRes.data || []) as CompanyRow[]);

      // Build driver_name → company_id map so we can bucket statements by
      // company. Multi-row driver entries (e.g. lease + company-driver) happen;
      // first match wins.
      const drRes = await supabase
        .from('drivers')
        .select('first_name, last_name, company_id');
      if (drRes.error) throw drRes.error;
      const dcMap: Record<string, string> = {};
      (drRes.data || []).forEach((d: any) => {
        const full = `${d.first_name || ''} ${d.last_name || ''}`.trim().toLowerCase();
        if (full && d.company_id && !dcMap[full]) dcMap[full] = d.company_id;
      });
      setDriverCompany(dcMap);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load 1099 data');
    } finally {
      setLoading(false);
    }
  }

  // --- Payer Information per company -----------------------------------
  function openPayerEditor(c: CompanyRow) {
    setEditingPayer(c.id);
    setPayerDraft({
      payer_tin: c.payer_tin || '',
      address: c.address || '',
      city: c.city || '',
      state: c.state || '',
      zip_code: c.zip_code || '',
      phone: c.phone || '',
    });
  }

  async function savePayerInfo() {
    if (!editingPayer) return;
    try {
      setSavingPayer(true);
      const { error } = await supabase
        .from('companies')
        .update({
          payer_tin: payerDraft.payer_tin.trim() || null,
          address: payerDraft.address.trim() || null,
          city: payerDraft.city.trim() || null,
          state: payerDraft.state.trim() || null,
          zip_code: payerDraft.zip_code.trim() || null,
          phone: payerDraft.phone.trim() || null,
        })
        .eq('id', editingPayer);
      if (error) throw error;
      toast.success('Payer info saved');
      setEditingPayer(null);
      loadAll();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save payer info');
    } finally {
      setSavingPayer(false);
    }
  }

  // Delete every statement for the given driver/business bucket in the current
  // year (and the matching imported PDFs in storage). Used when Accounting
  // wants to wipe a driver's data and re-import from scratch.
  async function deleteDriverBucket(bucket: { driverName: string; businessName: string | null; statementCount: number; rows: GeneratedContractorStatement[] }) {
    const label = bucket.businessName ? `${bucket.driverName} (${bucket.businessName})` : bucket.driverName;
    const ok = window.confirm(
      `Delete all ${bucket.statementCount} statement${bucket.statementCount === 1 ? '' : 's'} for ${label} in ${year}?\n\nThis also removes the source PDFs from storage and cannot be undone.`
    );
    if (!ok) return;
    try {
      const ids = bucket.rows.map(r => r.id);
      const paths = bucket.rows.map(r => r.imported_file_path).filter((p): p is string => !!p);
      if (paths.length > 0) {
        await supabase.storage.from(BUCKET).remove(paths).catch(() => { /* best effort */ });
      }
      // Release the billed loads BEFORE deleting. statement_line_items cascades on
      // delete, so afterwards there is no record of which loads these statements
      // claimed and they would stay stranded at included_in_statement = true —
      // invisible in the statement wizard and impossible to ever pay.
      const released = await releaseStatementLoads(ids);
      const { error } = await supabase.from('driver_statements').delete().in('id', ids);
      if (error) throw error;
      toast.success(
        `Deleted ${ids.length} statement${ids.length === 1 ? '' : 's'}` +
        (released > 0 ? ` — ${released} load${released === 1 ? '' : 's'} released for re-billing` : '')
      );
      loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete driver statements');
    }
  }

  // --- AI batch upload of legacy statement PDFs ------------------------
  // Find-or-create a `document_folders` row named "Statements for 1099" so
  // AI-imported PDFs show up in a predictable spot on the Documents page.
  async function ensureImportFolderId(): Promise<string | null> {
    try {
      const folderName = 'Statements for 1099';
      const existing = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', folderName)
        .is('parent_folder_id', null)
        .maybeSingle();
      if (existing.data?.id) return existing.data.id as string;
      const { data, error } = await supabase
        .from('document_folders')
        .insert({ name: folderName, parent_folder_id: null, is_system_folder: false })
        .select('id')
        .single();
      if (error) {
        console.warn('Could not create "Statements for 1099" folder:', error);
        return null;
      }
      return data?.id || null;
    } catch (err) {
      console.warn('ensureImportFolderId failed:', err);
      return null;
    }
  }

  async function handleAiBatchUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setAiBatchBusy(true);
    const { data: { user } } = await supabase.auth.getUser();
    const initial = Array.from(files).map(f => ({ file: f.name, state: 'pending' as const }));
    setAiBatchStatus(initial);

    // Resolve the Documents folder once for the whole batch.
    const importFolderId = await ensureImportFolderId();

    let doneCount = 0;
    let failedCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        setAiBatchStatus(s => s.map((r, idx) => idx === i ? { ...r, state: 'uploading' } : r));
        const safeName = file.name.replace(/[^\w.\-]/g, '_');
        const path = `1099/imports/${year}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { cacheControl: '3600', upsert: false });
        if (upErr) throw upErr;

        setAiBatchStatus(s => s.map((r, idx) => idx === i ? { ...r, state: 'parsing' } : r));
        const { data, error } = await supabase.functions.invoke('process-driver-statement', {
          body: {
            filePath: path,
            fileName: file.name,
            mimeType: file.type || 'application/pdf',
            uploadedBy: user?.id || null,
          },
        });
        if (error) {
          // Only treat 404 / boot failure as "not deployed". A generic non-2xx
          // almost always means the function ran but returned an error — in
          // that case we want the real message, not a misleading deploy hint.
          const raw = (error as any)?.message || String(error);
          const status = (error as any)?.context?.status;
          const looksMissing =
            status === 404 ||
            /Function not found|boot error/i.test(raw);
          if (looksMissing) {
            throw new Error('Edge function "process-driver-statement" not deployed. Run: npx supabase functions deploy process-driver-statement');
          }
          // Try to read the function's JSON body so the real error surfaces.
          let detail = raw;
          try {
            const resp: Response | undefined = (error as any)?.context?.response;
            if (resp) {
              const cloned = resp.clone();
              const text = await cloned.text();
              try {
                const parsed = JSON.parse(text);
                detail = parsed.error || parsed.message || text || raw;
              } catch {
                detail = text || raw;
              }
            }
          } catch { /* keep raw */ }
          console.error('Edge function returned error:', status, detail);
          throw new Error(`Import failed (${status || 'error'}): ${detail}`);
        }
        if (data?.error) throw new Error(data.error);
        if (!data?.statementId && !data?.success) {
          throw new Error('Edge function returned no statement (check function logs in Supabase).');
        }
        console.log('✅ Parsed statement:', data);

        // Register the uploaded PDF in the Documents system so it's browsable
        // on /documents under "Statements for 1099" — independent of the
        // driver_statements.imported_file_path column. Best effort: skip if
        // folder lookup failed or insert bounces for any reason.
        if (importFolderId) {
          const fileExt = (file.name.split('.').pop() || 'pdf').toLowerCase();
          await supabase.from('documents').insert({
            name: file.name,
            file_name: file.name,
            file_path: path,
            file_size: file.size,
            file_type: fileExt,
            mime_type: file.type || 'application/pdf',
            folder_id: importFolderId,
            uploaded_by: user?.id || null,
          }).then(res => {
            if (res.error) console.warn('documents insert failed:', res.error);
          });
        }

        doneCount++;
        setAiBatchStatus(s => s.map((r, idx) => idx === i ? { ...r, state: 'done' } : r));
      } catch (err: any) {
        const msg = err?.message || 'Failed';
        console.error(`❌ Import failed for ${file.name}:`, err);
        failedCount++;
        setAiBatchStatus(s => s.map((r, idx) => idx === i ? { ...r, state: 'error', error: msg } : r));
      }
    }
    setAiBatchBusy(false);

    // Summary toast based on the counts we tracked during the loop. Reading
    // from state inside a setState updater would violate render-phase rules
    // (can't update ToastProvider while another component is rendering).
    if (doneCount > 0 && failedCount === 0) {
      toast.success(`Imported ${doneCount} statement${doneCount === 1 ? '' : 's'}`);
    } else if (doneCount > 0 && failedCount > 0) {
      toast.info?.(`Imported ${doneCount}, ${failedCount} failed — see list below`);
    } else {
      toast.error(`All ${failedCount} file${failedCount === 1 ? '' : 's'} failed — see list below for details`);
    }
    loadAll();
  }
  // Roll up per (driver_name, type, business). A driver can appear twice if
  // they ran both as Company Driver and Owner Operator in the same year.
  const OWNER_TYPES = new Set(['owner', 'owner_operator', 'lease', 'rent', 'buy', 'contractor']);
  const perDriver = useMemo(() => {
    const q = search.trim().toLowerCase();
    type Bucket = {
      driverName: string;
      businessName: string | null;
      driverType: 'contractor' | 'owner_operator' | 'company_driver';
      commissionPct: number;  // 0 if not a percentage commission
      totalGross: number;
      totalExpenses: number;
      totalNet: number;
      statementCount: number;
      countImported: number;
      rows: GeneratedContractorStatement[];
    };
    const map = new Map<string, Bucket>();

    for (const s of generated) {
      const driverKey = (s.driver_name || '').trim().toLowerCase();
      const compId = driverCompany[driverKey] || null;
      if (selectedCompanyTab === UNASSIGNED_TAB && compId) continue;
      if (selectedCompanyTab !== ALL_TAB && selectedCompanyTab !== UNASSIGNED_TAB && compId !== selectedCompanyTab) continue;

      const info = driverInfoMap[driverKey];
      const tax = taxInfoMap[driverKey];
      // Classify, preferring the most specific signal we have:
      //   scheduled_deductions.lease_or_rent === 'contractor'     → Contractor
      //   lease_or_rent in other OWNER_TYPES                      → Owner Operator
      //   driver_1099_info.use_contractor_on_1099 = true          → Contractor
      //   driver_1099_info.contractor_name set (import or edit)   → Contractor
      //   contractor_name on the driver_statements row            → Owner Operator
      //   otherwise                                               → Company Driver
      const arrangement = (info?.lease_or_rent || '').toLowerCase();
      const taxContractorName = tax?.contractor_name?.trim() || null;
      let driverType: 'contractor' | 'owner_operator' | 'company_driver' = 'company_driver';
      let businessName: string | null = null;
      if (arrangement === 'contractor') {
        driverType = 'contractor';
        businessName = info?.contractor_name?.trim() || taxContractorName || (s.contractor_name || '').trim() || null;
      } else if (info && OWNER_TYPES.has(arrangement)) {
        driverType = 'owner_operator';
        businessName = info.contractor_name?.trim() || taxContractorName || (s.contractor_name || '').trim() || null;
      } else if (tax?.use_contractor_on_1099 || taxContractorName) {
        driverType = 'contractor';
        businessName = taxContractorName;
      } else if (s.contractor_name && s.contractor_name.trim()) {
        driverType = 'owner_operator';
        businessName = s.contractor_name.trim();
      }

      // Parse commission to a percentage value (0 = N/A). Percentage is
      // indicated by a trailing '%' or a magnitude > 100 (legacy).
      let commissionPct = 0;
      if (driverType !== 'company_driver' && info?.commission_cpm) {
        const raw = info.commission_cpm.toString().trim();
        const num = parseFloat(raw.replace('%', ''));
        if (raw.includes('%') || num > 100) commissionPct = num;
      }

      const label = s.driver_name || 'Unknown';
      if (q && !label.toLowerCase().includes(q) && !(businessName || '').toLowerCase().includes(q)) continue;

      const key = `${label.toLowerCase()}|${driverType}|${(businessName || '').toLowerCase()}`;
      let b = map.get(key);
      if (!b) {
        b = {
          driverName: label,
          businessName,
          driverType,
          commissionPct,
          totalGross: 0,
          totalExpenses: 0,
          totalNet: 0,
          statementCount: 0,
          countImported: 0,
          rows: [],
        };
        map.set(key, b);
      }

      const gross = Number(s.total_revenue ?? s.net_pay ?? 0);
      const expenses = Number((s.total_deductions ?? 0)) + Number((s.total_fuel ?? 0));
      b.totalGross += gross;
      b.totalExpenses += expenses;
      b.totalNet += Number(s.net_pay || 0);
      b.statementCount += 1;
      if (s.imported_file_name) b.countImported += 1;
      b.rows.push(s);
    }
    return Array.from(map.values()).sort((a, b) => b.totalGross - a.totalGross);
  }, [generated, driverInfoMap, taxInfoMap, driverCompany, selectedCompanyTab, search]);

  const totalStatementsCount = useMemo(
    () => perDriver.reduce((s, g) => s + g.statementCount, 0),
    [perDriver]
  );

  // Which company (if any) is the currently-selected tab? Used to render
  // the Payer Information card.
  const activeCompany = useMemo(() => {
    if (selectedCompanyTab === ALL_TAB || selectedCompanyTab === UNASSIGNED_TAB) return null;
    return companies.find(c => c.id === selectedCompanyTab) || null;
  }, [companies, selectedCompanyTab]);

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y--) out.push(y);
    return out;
  }, [currentYear]);

  // Stats summary based on the currently selected year + tab + search.
  const stats = useMemo(() => {
    const drivers = perDriver.length;
    const totalNet = perDriver.reduce((s, g) => s + g.totalNet, 0);
    const imported = perDriver.reduce((s, g) => s + g.countImported, 0);
    return { drivers, totalNet, imported };
  }, [perDriver]);

  const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '?';

  return (
    <div className="space-y-6 p-6 bg-slate-50 min-h-full">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-800 rounded-2xl shadow-lg p-6 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="bg-white/15 backdrop-blur-sm p-3 rounded-xl">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">1099s</h2>
              <p className="text-blue-100 text-sm">Upload statements, manage payers, and generate 1099-NECs per driver.</p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl flex items-center gap-3">
            <label className="text-xs uppercase text-blue-100 tracking-wider">Tax Year</label>
            <select
              value={year}
              onChange={e => setYear(parseInt(e.target.value))}
              className="bg-transparent text-white text-lg font-semibold outline-none [&>option]:text-slate-900"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Drivers</p>
            <p className="text-2xl font-bold text-slate-900">{stats.drivers}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600">
            <Users className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Year-to-date net</p>
            <p className="text-2xl font-bold text-emerald-700">
              ${stats.totalNet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600">
            <FileText className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">AI-imported</p>
            <p className="text-2xl font-bold text-purple-700">{stats.imported}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-purple-50 text-purple-600">
            <Sparkles className="w-6 h-6" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {/* AI batch-import of legacy driver-statement PDFs */}
          <AiImportCard
            busy={aiBatchBusy}
            status={aiBatchStatus}
            onFiles={files => handleAiBatchUpload(files)}
          />

          {/* Company tab bar */}
          <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
            <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-200 bg-slate-50">
              {[
                { id: ALL_TAB, label: 'All companies' },
                ...companies.map(c => ({ id: c.id, label: c.name })),
                { id: UNASSIGNED_TAB, label: 'Unassigned' },
              ].map(t => {
                const active = selectedCompanyTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedCompanyTab(t.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Payer Information card — only shown when a specific company tab is selected */}
            {activeCompany && (
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Payer Information (1099-NEC)</h3>
                    <p className="text-xs text-slate-500">Shown in the PAYER block on every 1099 generated for drivers in this company.</p>
                  </div>
                  {editingPayer !== activeCompany.id ? (
                    <button
                      onClick={() => openPayerEditor(activeCompany)}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingPayer(null)}
                        className="px-3 py-1 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={savePayerInfo}
                        disabled={savingPayer}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingPayer ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Save
                      </button>
                    </div>
                  )}
                </div>
                {editingPayer !== activeCompany.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">PAYER'S TIN</p>
                      <p className="text-slate-900 font-mono">{activeCompany.payer_tin || <span className="text-slate-400 italic">Not set</span>}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">Payer's name, address, city, state, ZIP, phone</p>
                      <div className="text-slate-900">
                        <p className="font-semibold">{activeCompany.name}</p>
                        {activeCompany.address && <p>{activeCompany.address}</p>}
                        {(activeCompany.city || activeCompany.state || activeCompany.zip_code) && (
                          <p>
                            {[activeCompany.city, activeCompany.state, activeCompany.zip_code].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {activeCompany.phone && <p className="text-slate-600">{activeCompany.phone}</p>}
                        {!activeCompany.address && !activeCompany.city && !activeCompany.state && !activeCompany.zip_code && !activeCompany.phone && (
                          <p className="text-slate-400 italic">Address and phone not set</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs uppercase tracking-wider text-slate-500">PAYER'S TIN</span>
                      <input
                        type="text"
                        value={payerDraft.payer_tin}
                        onChange={e => setPayerDraft(d => ({ ...d, payer_tin: e.target.value }))}
                        placeholder="e.g. 85-3654481"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono"
                      />
                    </label>
                    <label className="block md:col-span-1">
                      <span className="text-xs uppercase tracking-wider text-slate-500">Phone</span>
                      <input
                        type="text"
                        value={payerDraft.phone}
                        onChange={e => setPayerDraft(d => ({ ...d, phone: e.target.value }))}
                        placeholder="e.g. 219-226-3142"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-xs uppercase tracking-wider text-slate-500">Street address</span>
                      <input
                        type="text"
                        value={payerDraft.address}
                        onChange={e => setPayerDraft(d => ({ ...d, address: e.target.value }))}
                        placeholder="e.g. 1900 Lake St, Suite 1"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-wider text-slate-500">City</span>
                      <input
                        type="text"
                        value={payerDraft.city}
                        onChange={e => setPayerDraft(d => ({ ...d, city: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-xs uppercase tracking-wider text-slate-500">State</span>
                        <input
                          type="text"
                          maxLength={2}
                          value={payerDraft.state}
                          onChange={e => setPayerDraft(d => ({ ...d, state: e.target.value.toUpperCase() }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm uppercase"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs uppercase tracking-wider text-slate-500">ZIP</span>
                        <input
                          type="text"
                          value={payerDraft.zip_code}
                          onChange={e => setPayerDraft(d => ({ ...d, zip_code: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-slate-800">
                  Drivers ({perDriver.length})
                  <span className="text-slate-400 font-normal"> • {totalStatementsCount.toLocaleString()} Statements</span>
                </h3>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by driver or contractor…"
                    className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <span className="text-xs text-slate-500">
                  Grand total gross:
                  <span className="font-semibold text-slate-900 ml-1">
                    ${perDriver.reduce((s, g) => s + g.totalGross, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </span>
              </div>
            </div>
            {perDriver.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No statements in {year} yet. Generate one on the Statements page or upload a past PDF above.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {perDriver.map((g, idx) => {
                  const afterCommission = g.commissionPct > 0
                    ? g.totalGross * (1 - g.commissionPct / 100)
                    : null;
                  const open = () => {
                    window.history.pushState({}, '', `/company-1099/${encodeURIComponent(g.driverName)}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  };
                  return (
                    <div
                      key={`${g.driverName}-${g.driverType}-${g.businessName || ''}-${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                      className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 px-4 py-4 cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-semibold shadow-sm shrink-0 ${
                          g.driverType === 'contractor'
                            ? 'bg-gradient-to-br from-purple-500 to-fuchsia-600'
                            : g.driverType === 'owner_operator'
                              ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                              : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                        }`}>
                          {initials(g.driverName)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{g.driverName}</p>
                          {g.businessName && (
                            <p className="text-xs text-slate-500 truncate">{g.businessName}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${
                              g.driverType === 'contractor'
                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                : g.driverType === 'owner_operator'
                                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                  : 'bg-blue-100 text-blue-800 border border-blue-200'
                            }`}>
                              {g.driverType === 'contractor' ? 'Contractor' : g.driverType === 'owner_operator' ? 'Owner Operator' : 'Company Driver'}
                            </span>
                            <span className="text-xs text-slate-500">
                              {g.statementCount} statement{g.statementCount === 1 ? '' : 's'}
                            </span>
                            {g.countImported > 0 && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                <Sparkles className="w-2.5 h-2.5" /> {g.countImported} imported
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="md:text-right">
                        <div className="text-sm space-y-0.5">
                          <div className="flex md:justify-end gap-3">
                            <span className="text-slate-500">Total Gross:</span>
                            <span className="font-semibold text-slate-900 tabular-nums">
                              ${g.totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          {afterCommission !== null && (
                            <div className="flex md:justify-end gap-3">
                              <span className="text-slate-500">After {g.commissionPct}%:</span>
                              <span className="font-medium text-emerald-700 tabular-nums">
                                ${afterCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          )}
                          <div className="flex md:justify-end gap-3">
                            <span className="text-slate-500">Total Expenses:</span>
                            <span className="font-medium text-red-700 tabular-nums">
                              ${g.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex md:justify-end items-center gap-3">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteDriverBucket(g); }}
                            title={`Delete ${g.driverName}'s ${year} statements`}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 hover:underline"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            View Details <ChevronRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type AiImportStatus = { file: string; state: 'pending' | 'uploading' | 'parsing' | 'done' | 'error'; error?: string };

function AiImportCard({
  busy,
  status,
  onFiles,
}: {
  busy: boolean;
  status: AiImportStatus[];
  onFiles: (files: FileList | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const doneCount = status.filter(s => s.state === 'done').length;
  const errorCount = status.filter(s => s.state === 'error').length;
  const inFlight = status.length - doneCount - errorCount;

  function pickPdfs(list: FileList | null): FileList | null {
    if (!list) return null;
    const pdfs = Array.from(list).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) return null;
    const dt = new DataTransfer();
    pdfs.forEach(f => dt.items.add(f));
    return dt.files;
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-xl shadow border border-purple-100 overflow-hidden">
      <div className="flex items-start gap-3 px-5 py-4 border-b border-purple-100/70">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center shadow-sm shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Import past statements with AI</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Drop multiple PDFs — same Document AI + OpenAI pipeline used for rate confirmations. Each
            statement is parsed and filed under the matching driver automatically.
          </p>
        </div>
      </div>

      <div className="p-5">
        <div
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!busy) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            if (busy) return;
            const picked = pickPdfs(e.dataTransfer.files);
            onFiles(picked);
          }}
          className={`relative rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            busy
              ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
              : dragging
                ? 'border-purple-500 bg-purple-50'
                : 'border-purple-300 bg-white hover:border-purple-400 hover:bg-purple-50/50'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="application/pdf"
            disabled={busy}
            onChange={e => {
              const picked = pickPdfs(e.target.files);
              onFiles(picked);
              e.target.value = '';
            }}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              busy ? 'bg-slate-100 text-slate-400' : 'bg-purple-100 text-purple-600'
            }`}>
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <UploadCloud className="w-6 h-6" />}
            </div>
            <p className="text-sm font-medium text-slate-800">
              {busy ? 'Processing…' : 'Drop PDF statements here or click to browse'}
            </p>
            <p className="text-xs text-slate-500">PDF only · multiple files supported</p>
            {!busy && (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700"
              >
                <FileUp className="w-3.5 h-3.5" /> Choose files
              </button>
            )}
          </div>
        </div>

        {status.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
              <span className="font-medium">
                {status.length} file{status.length === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-3">
                {doneCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {doneCount} done
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-600">
                    <XCircle className="w-3.5 h-3.5" /> {errorCount} failed
                  </span>
                )}
                {inFlight > 0 && (
                  <span className="inline-flex items-center gap-1 text-blue-700">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {inFlight} in progress
                  </span>
                )}
              </div>
            </div>
            <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {status.map((r, idx) => (
                <li key={idx} className="flex items-center justify-between px-3 py-2 text-xs">
                  <span className="truncate max-w-[60%] text-slate-700" title={r.file}>{r.file}</span>
                  <span className={`inline-flex items-center gap-1 font-medium ${
                    r.state === 'done' ? 'text-emerald-700'
                    : r.state === 'error' ? 'text-red-600'
                    : r.state === 'parsing' ? 'text-purple-700'
                    : r.state === 'uploading' ? 'text-blue-700'
                    : 'text-slate-500'
                  }`}>
                    {r.state === 'done' && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {r.state === 'error' && <XCircle className="w-3.5 h-3.5" />}
                    {(r.state === 'parsing' || r.state === 'uploading') && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {r.state === 'error' ? `error — ${r.error || 'failed'}` : r.state}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
